import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Home, FolderOpen, Database, Workflow, Settings, Shield, Droplets, LogOut, Bot, Server, Wrench, CreditCard, ChevronRight } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { NavigationLandmark } from "@/components/navigation";
import { useAuth, useAuthStore } from "@/stores/auth/auth-store";
import { useOrganizationStore } from "@/stores/organization/organization-store";
import { canManageOrganization } from "@/utils/organizationPermissions";
import { PendingInvitationsDropdown } from "@/components/invitations/PendingInvitationsDropdown";
import { PendingSponsorshipsDropdown } from "@/components/account/PendingSponsorshipsDropdown";
import { PendingConnectionsDropdown } from "@/components/connections/PendingConnectionsDropdown";
import { MessagesDropdown } from "@/components/messages/MessagesDropdown";


interface NavigationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSignOut: () => void;
}

/**
 * Shared navigation drawer used by both RootLayout and ViewerLayout.
 */
export const NavigationDrawer: React.FC<NavigationDrawerProps> = ({
  isOpen,
  onClose,
  onSignOut,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const displayName = user
    ? `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || user.username
    : "User";
  const initials = user
    ? `${(user.firstName?.[0] ?? "").toUpperCase()}${(user.lastName?.[0] ?? "").toUpperCase()}` || user.username?.[0]?.toUpperCase() || "U"
    : "U";

  const handleProfileClick = () => {
    onClose();
    navigate("/profile");
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[60]"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Drawer */}
      <NavigationLandmark label="Main navigation">
        <aside
          className={cn(
            "fixed top-0 left-0 h-full w-80 bg-background border-r border-border z-[70]",
            "transform transition-transform duration-300 ease-in-out shadow-xl",
            isOpen ? "translate-x-0" : "-translate-x-full",
          )}
        >
          <div className="flex flex-col h-full">
            {/* Profile Header */}
            <div className="border-b border-border">
              <button
                onClick={handleProfileClick}
                className="w-full flex items-center gap-3.5 p-5 hover:bg-accent/50 transition-colors text-left group"
                aria-label="Open profile settings"
              >
                <Avatar className="h-11 w-11 ring-2 ring-primary/20 group-hover:ring-primary/40 transition-all">
                  {user?.avatar ? (
                    <AvatarImage src={user.avatar} alt={displayName} />
                  ) : null}
                  <AvatarFallback className="bg-primary/10 text-primary font-semibold text-sm">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">{displayName}</p>
                  {user?.email && (
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                  )}
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground/60 group-hover:text-muted-foreground transition-colors flex-shrink-0" />
              </button>
            </div>

            {/* Quick Actions Row */}
            <div className="border-b border-border px-5 py-3 flex items-center justify-around">
              <PendingInvitationsDropdown />
              <PendingSponsorshipsDropdown />
              <PendingConnectionsDropdown />
              <MessagesDropdown />
            </div>

            <nav className="flex-1 overflow-y-auto p-4">
              <NavItems onItemClick={onClose} />
            </nav>
            <div className="p-4 border-t border-border">
              <button
                onClick={() => { onClose(); onSignOut(); }}
                className="flex items-center gap-3 px-3 py-2 w-full rounded-md text-destructive hover:bg-destructive/10 transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <LogOut className="h-5 w-5" />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </aside>
      </NavigationLandmark>
    </>
  );
};

/**
 * Navigation items with org-aware links and admin conditional.
 */
const NavItems: React.FC<{ onItemClick?: () => void }> = ({ onItemClick }) => {
  const {
    platformAdminEntitlement,
    currentContext,
    organizations,
    fetchOrganizations,
  } =
    useOrganizationStore();
  const isProjectAdmin = useAuthStore((s) => s.isProjectAdmin);
  const isOrgAdmin = useAuthStore((s) => s.isOrgAdmin);
  const isServiceProvider = useAuthStore((s) => s.isServiceProvider);
  const isAdmin = platformAdminEntitlement || isProjectAdmin || isOrgAdmin;
  const activeOrganization =
    currentContext?.type === "organization" && currentContext.orgSlug
      ? organizations.find((org) => org.slug === currentContext.orgSlug)
      : null;
  const activeOrgRole = activeOrganization?.membership?.role;
  const canManageActiveOrg = canManageOrganization(activeOrgRole);

  useEffect(() => {
    if (currentContext?.type === "organization" && organizations.length === 0) {
      fetchOrganizations().catch((error) => {
        console.error("Failed to load organizations for navigation", error);
      });
    }
  }, [
    currentContext?.orgSlug,
    currentContext?.type,
    fetchOrganizations,
    organizations.length,
  ]);

  // Build context-aware Projects link
  const projectsHref =
    currentContext?.type === "organization" && currentContext?.orgSlug
      ? `/orgs/${currentContext.orgSlug}/projects`
      : "/projects";

  const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
    Home,
    FolderOpen,
    Workflow,
    Database,
    Shield,
    Droplets,
    Bot,
    Server,
    Wrench,
    CreditCard,

    Settings,
  };

  const navItems = [
    { label: "Dashboard", href: "/dashboard", icon: "Home" },
    { label: "Projects", href: projectsHref, icon: "FolderOpen" },
    ...(currentContext?.type === "organization" &&
    currentContext.orgSlug &&
    activeOrganization &&
    canManageActiveOrg
      ? [
          {
            label: "Org Settings",
            href: `/orgs/${currentContext.orgSlug}/settings`,
            icon: "Settings",
          },
        ]
      : []),

    { label: "Workflow", href: "/workflow-configuration", icon: "Workflow" },
    { label: "Personal Settings", href: "/settings", icon: "Settings" },

    { label: "Data", href: "/data", icon: "Database" },
    { label: "Agents", href: "/agents", icon: "Bot" },
    { label: "Infrastructure", href: "/infrastructure", icon: "Server" },
    { label: "Irrigation", href: "/irrigation", icon: "Droplets" },
    ...(isOrgAdmin ? [{ label: "Billing", href: "/billing", icon: "CreditCard" }] : []),
    ...(isServiceProvider ? [{ label: "Service Provider Dashboard", href: "/service-provider/dashboard", icon: "Wrench" }] : []),
    ...(isAdmin ? [{ label: "Admin", href: "/admin", icon: "Shield" }] : []),
  ];

  return (
    <ul className="space-y-2" role="list">
      {navItems.map((item) => {
        const IconComponent = iconMap[item.icon];
        return (
          <li key={item.href}>
            <Link
              to={item.href}
              onClick={onItemClick}
              className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-accent
                         transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {IconComponent && <IconComponent className="h-5 w-5" />}
              <span>{item.label}</span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
};
